import {
  Alert,
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useIsMobile } from "../../../hook/useIsMobile";
import CustomDatePicker from "../../datePicker/customDatePicker.component";
import { useCallback, useEffect, useState } from "react";
import { checkDateRangeOverlap } from "./utils";
import dayjs from "dayjs";

const CropHistoryRow = ({
  onSubmit,
  theme,
  onDelete,
  crops,
  initialData,
  ranges,
  formData,
}) => {
  const isMobile = useIsMobile();
  const today = new Date();
  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
    reset,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      colture: initialData?.colture ?? "",
      start_date: initialData?.start_date ?? null,
      end_date: initialData?.end_date ?? null,
    },
  });
  const watchedValues = watch();
  const colture = watch("colture");

  const [startDate, setStartDate] = useState(
    initialData?.start_date ? new Date(initialData.start_date) : null
  );
  const [endDate, setEndDate] = useState(
    initialData?.end_date ? new Date(initialData.end_date) : null
  );
  const [overlapWarning, setOverlapWarning] = useState("");

  const handleStartDateSelect = (date) => {
    setStartDate(date);
    setValue("start_date", date ? dayjs(date).format("YYYY-MM-DD") : null);

    if (colture) {
      trigger(["start_date", "end_date"]);
    }
  };

  const handleEndDateSelect = (date) => {
    setEndDate(date);
    setValue("end_date", date ? dayjs(date).format("YYYY-MM-DD") : null);

    if (colture) {
      trigger(["start_date", "end_date"]);
    }
  };

  useEffect(() => {
    const currentData = {
      colture: watchedValues.colture || "",
      start_date: watchedValues.start_date || null,
      end_date: watchedValues.end_date || null,
    };

    if (
      JSON.stringify(currentData) !==
      JSON.stringify({
        colture: initialData?.colture || "",
        start_date: initialData?.start_date || null,
        end_date: initialData?.end_date || null,
      })
    ) {
      if (colture) {
        trigger().then(() => {
          onSubmit(currentData);
        });
      } else {
        onSubmit(currentData);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues.colture, watchedValues.start_date, watchedValues.end_date]);

  const validateStartDate = useCallback(
    (value) => {
      if (colture && !value) {
        return "Data di inizio obbligatoria quando è selezionata una coltura";
      }

      if (value && endDate) {
        const hasOverlap = checkDateRangeOverlap(value, endDate, ranges);
        if (hasOverlap) {
          setOverlapWarning(
            "Attenzione: la data si sovrappone con altre già esistenti"
          );
        } else {
          setOverlapWarning("");
        }
      }
      return true;
    },
    [colture, endDate, ranges]
  );

  const validateEndDate = useCallback(
    (value) => {
      if (colture && !value) {
        return "Data di fine obbligatoria quando è selezionata una coltura";
      }

      if (startDate && value) {
        const hasOverlap = checkDateRangeOverlap(startDate, value, ranges);
        if (hasOverlap) {
          setOverlapWarning(
            "Attenzione: la data si sovrappone con altre già esistenti"
          );
        } else {
          setOverlapWarning("");
        }
      }
      return true;
    },
    [colture, startDate, ranges]
  );

  useEffect(() => {
    register("start_date", { validate: validateStartDate });
    register("end_date", { validate: validateEndDate });
  }, [colture, register, validateEndDate, validateStartDate]);

  useEffect(() => {
    if (initialData) {
      reset({
        colture: initialData.colture ?? "",
        start_date: initialData.start_date ?? null,
        end_date: initialData.end_date ?? null,
      });

      setStartDate(
        initialData.start_date ? new Date(initialData.start_date) : null
      );
      setEndDate(initialData.end_date ? new Date(initialData.end_date) : null);
    }
  }, [initialData, reset]);

  return (
    <Box
      width="100%"
      height="auto"
      bgcolor={theme.palette.custom.backgroundColor}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
      >
        <Typography fontWeight="bold" fontSize="14px">
          Coltura
        </Typography>
        <Button
          variant="text"
          sx={{ textTransform: "none", textDecoration: "underline" }}
          size="small"
          onClick={onDelete}
        >
          Elimina
        </Button>
      </Box>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        alignItems="center"
        width="100%"
        p={2}
        gap={4}
      >
        <TextField
          variant="filled"
          select
          defaultValue={initialData?.colture?.id || initialData?.colture || ""}
          sx={{
            "& .MuiFilledInput-root": {
              height: "50px",
              backgroundColor: "white",
            },
          }}
          label="Coltura"
          fullWidth
          {...register("colture", {
            required: "Coltura obbligatoria",
          })}
          error={!!errors?.colture}
          helperText={errors?.colture?.message}
        >
          {crops && crops.length > 0 ? (
            crops.map((culture) => (
              <MenuItem key={culture.id} value={culture.id}>
                {culture.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled value="">
              Nessuna opzione disponibile
            </MenuItem>
          )}
        </TextField>
        <Box display="flex" gap={2} width="100%">
          <CustomDatePicker
            label="Data di inizio"
            id="start_date"
            selectedDate={startDate}
            handleDateSelect={handleStartDateSelect}
            error={errors?.start_date?.message}
            customStyle={{
              "& .MuiFilledInput-root": {
                height: "50px",
                backgroundColor: "white",
              },
            }}
            disabledDays={{ after: today }}
            disabledRanges={ranges}
            disabled={!colture}
          />
          <CustomDatePicker
            label="Data di fine"
            id="end_date"
            selectedDate={endDate}
            handleDateSelect={handleEndDateSelect}
            error={errors?.end_date?.message}
            disabledDays={{ before: startDate, after: today }}
            disabledRanges={ranges}
            customStyle={{
              "& .MuiFilledInput-root": {
                height: "50px",
                backgroundColor: "white",
              },
            }}
            disabled={!colture}
          />
        </Box>
      </Box>
      {overlapWarning && (
        <Alert severity="warning" sx={{ width: "auto", margin: 2 }}>
          {overlapWarning}
        </Alert>
      )}
    </Box>
  );
};

export default CropHistoryRow;
